import React, { createContext, useMemo } from 'react';
import { TFunction } from 'i18next';
import { Item, ItemModel, PrimaryCategoryModel, StoreModel, CountZoneTypeResponse } from '../../../app/services/api/generated';
import { useStockCountZone } from '../hooks/UseStockCountZone';
import { Blocker } from 'react-router';

export interface StockCountZonesContextValues {
    t: TFunction<'stockCountZonesWithItems', undefined>;
    isLoading: boolean;
    isSubmitting: boolean;
    isCategoriesModalOpen: boolean,
    isCategoryDeleteModalOpen: boolean,
    isItemsModalOpen: boolean,
    isItemsDeleteModalOpen: boolean,
    isBulkModalOpen: boolean,
    isCreating: boolean,
    zoneName: string;
    setZoneName: (name: string) => void;
    submitCountZone: () => Promise<boolean>;
    handlePageSave: () => Promise<void>;
    categoryData: PrimaryCategoryModel[];
    itemsData: ItemModel[];
    handleLeavePageModalClose: () => void;
    handleLeavePageModalConfirm: () => void;
    stores: StoreModel[];
    selectedStores: string[];
    setSelectedStores: (stores: string[]) => void;
    firstSelectedStore?: StoreModel;
    handleAddCategoriesClick: () => void,
    handleCategories: (data: PrimaryCategoryModel[]) => void,
    deleteCategoryRow: (idToDelete?: number) => void;
    deleteCategories: (data: PrimaryCategoryModel[], deletedCategoryIds: number[]) => void,
    handleClickOpen: () => void,
    handleClose: () => void,
    handleAddBulkItemClick: () => void,
    handleAddItemsClick: () => void,
    deleteItems: () => void,
    deleteItemRow: (itemNumberToDelete?: string) => void,
    handleItems: (data: Item[], removedItemNumbers?: string[]) => void,
    handleLeaveClick: () => void;
    reorderItems: (updatedItems: ItemModel[]) => void,
    closeSnackBar: () => void,
    isSnackBarOpen: boolean,
    snackBarSeverity: 'error' | 'warning' | 'info' | 'success',
    snackbarMessage: string,
    snackBarDuration: number | null,
    categoryIdToDelete: number | undefined,
    blocker: Blocker,
    hasUserReorderedItems: boolean,
    countZoneTypes: CountZoneTypeResponse[],
    selectedCountZoneType: number | undefined,
    setSelectedCountZoneType: (typeId: number | undefined) => void
}

export const StockCountZonesContext = createContext<null | StockCountZonesContextValues>(null);

interface ProviderProps {
    children: React.ReactNode;
}

export const StockCountZonesProvider = ({ children }: ProviderProps) => {
  const {
    t,
    isLoading,
    isSubmitting,
    zoneName,
    setZoneName,
    submitCountZone,
    handlePageSave,
    categoryData,
    itemsData,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    stores,
    selectedStores,
    setSelectedStores,
    firstSelectedStore,
    handleAddCategoriesClick,
    handleCategories,
    isCategoriesModalOpen,
    deleteCategoryRow,
    deleteCategories,
    handleClickOpen,
    handleClose,
    isCategoryDeleteModalOpen,
    handleAddBulkItemClick,
    handleAddItemsClick,
    deleteItems,
    deleteItemRow,
    isItemsModalOpen,
    handleItems,
    handleLeaveClick,
    reorderItems,
    isItemsDeleteModalOpen,
    isBulkModalOpen,
    isCreating,
    closeSnackBar,
    isSnackBarOpen,
    snackBarSeverity,
    snackbarMessage,
    snackBarDuration,
    blocker,
    categoryIdToDelete,
    hasUserReorderedItems,
    countZoneTypes,
    selectedCountZoneType,
    setSelectedCountZoneType,
  } = useStockCountZone();

  const value: StockCountZonesContextValues = {
    t,
    isLoading,
    isSubmitting,
    zoneName,
    setZoneName,
    submitCountZone,
    handlePageSave,
    categoryData,
    itemsData,
    handleLeavePageModalClose,
    handleLeavePageModalConfirm,
    stores,
    selectedStores,
    setSelectedStores,
    firstSelectedStore,
    handleAddCategoriesClick,
    handleCategories,
    isCategoriesModalOpen,
    deleteCategoryRow,
    deleteCategories,
    handleClickOpen,
    handleClose,
    isCategoryDeleteModalOpen,
    handleAddBulkItemClick,
    handleAddItemsClick,
    deleteItems,
    deleteItemRow,
    isItemsModalOpen,
    handleItems,
    handleLeaveClick,
    reorderItems,
    isItemsDeleteModalOpen,
    isBulkModalOpen,
    isCreating,
    closeSnackBar,
    isSnackBarOpen,
    snackBarSeverity,
    snackbarMessage,
    snackBarDuration,
    blocker,
    categoryIdToDelete,
    hasUserReorderedItems,
    countZoneTypes,
    selectedCountZoneType,
    setSelectedCountZoneType,
  };

  const memoizedValue = useMemo(() => value, Object.values(value));

  return (
    <StockCountZonesContext.Provider value={memoizedValue}>
      {children}
    </StockCountZonesContext.Provider>
  );
};

